import { apiClient } from './client'
import type {
  CreationCreateRequest,
  CreationTaskDetail,
  CreationTaskFilters,
  CreationTaskList,
  CreationTaskSummary,
  CreationWorkFilters,
  CreationWorkList,
  ApiWorkSummary,
} from '@/lib/types/creation'

const BASE = '/agent-api/creation'

// ── Pipeline (background graph) types ────────────────────────────────

export type PipelineStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'partially_succeeded'
  | 'failed'
  | 'canceled'

export interface PipelineSummary {
  id: string
  pipeline_type: string
  title: string
  status: PipelineStatus
  current_step?: string | null
  step_index?: number
  total_steps?: number
  progress: number
  error_code?: string | null
  error_message?: string | null
  created_at: string
  updated_at: string
  progress_config?: Array<{
    step: string
    index: number
    progress: number
    duration_sec?: number
  }> | null
}

export interface PipelineDubbingResult {
  audio_path?: string
  file_size?: number
  duration_ms?: number
  sample_rate?: number
  segment_count?: number
  checksum_sha256?: string
  segments?: Array<{ index: number; char_count?: number }>
}

export interface PipelineDetail extends PipelineSummary {
  input_params: Record<string, unknown>
  result: {
    dubbing?: PipelineDubbingResult | null
    progress_config?: PipelineSummary['progress_config']
    [key: string]: unknown
  } | null
  child_tasks: Array<{
    step: string
    status: string
    progress: number
    error?: string | null
  }>
}

export interface PipelineCreateResult {
  pipeline_id: string
  status: string
  created_at: string
}

export interface CreatePipelinePayload {
  pipeline_type: string
  title?: string
  input_params?: Record<string, unknown>
  input_assets?: Array<Record<string, unknown>>
  idempotency_key?: string
}

export const creationApi = {
  // ── Tasks ──────────────────────────────────────────────────

  createTask: async (payload: CreationCreateRequest) => {
    const response = await apiClient.post<CreationTaskSummary>(
      `${BASE}/tasks`,
      payload,
    )
    return response.data
  },

  getTask: async (id: string) => {
    const response = await apiClient.get<CreationTaskDetail>(
      `${BASE}/tasks/${id}`,
    )
    return response.data
  },

  listTasks: async (params: CreationTaskFilters = {}) => {
    const response = await apiClient.get<CreationTaskList>(
      `${BASE}/tasks`,
      { params },
    )
    return response.data
  },

  cancelTask: async (id: string) => {
    const response = await apiClient.post<CreationTaskSummary>(
      `${BASE}/tasks/${id}/cancel`,
    )
    return response.data
  },

  retryTask: async (id: string) => {
    const response = await apiClient.post<CreationTaskSummary>(
      `${BASE}/tasks/${id}/retry`,
    )
    return response.data
  },

  deleteTask: async (id: string) => {
    await apiClient.delete(`${BASE}/tasks/${id}`)
  },

  // ── Works ──────────────────────────────────────────────────

  listWorks: async (params: CreationWorkFilters = {}) => {
    const response = await apiClient.get<CreationWorkList>(
      `${BASE}/works`,
      { params },
    )
    return response.data
  },

  getWork: async (id: string) => {
    const response = await apiClient.get<ApiWorkSummary>(
      `${BASE}/works/${id}`,
    )
    return response.data
  },

  setFavorite: async (id: string, favorite: boolean) => {
    const response = await apiClient.put<ApiWorkSummary>(
      `${BASE}/works/${id}/favorite`,
      { favorite },
    )
    return response.data
  },

  deleteWork: async (id: string) => {
    await apiClient.delete(`${BASE}/works/${id}`)
  },

  // ── Prompts ──────────────────────────────────────────────────

  listPrompts: async () => {
    const response = await apiClient.get<import('@/lib/types/creation').CreationPrompt[]>(
      '/agent-api/creation/prompts',
    )
    return response.data
  },

  updatePrompt: async (
    id: string,
    data: { name?: string; description?: string; content?: string; version?: number },
  ) => {
    const response = await apiClient.put<import('@/lib/types/creation').CreationPrompt>(
      `/agent-api/creation/prompts/${id}`,
      data,
    )
    return response.data
  },

  previewPrompt: async (content: string, variables: Record<string, string>) => {
    const response = await apiClient.post<{
      rendered: string; variables: string[]; valid: boolean; error: string
    }>('/agent-api/creation/prompts/preview', { content, variables })
    return response.data
  },

  restoreDefaultPrompt: async (id: string) => {
    const response = await apiClient.post<import('@/lib/types/creation').CreationPrompt>(
      `/agent-api/creation/prompts/${id}/restore-default`,
    )
    return response.data
  },

  listPromptVersions: async (id: string) => {
    const response = await apiClient.get<import('@/lib/types/creation').CreationPromptRevision[]>(
      `/agent-api/creation/prompts/${id}/versions`,
    )
    return response.data
  },

  generatePrompts: async (
    id: string,
    content: string,
    variables: Record<string, string>,
    assets: { download_url: string; preview_url?: string; name: string; mime_type?: string; width?: number | null; height?: number | null }[],
  ) => {
    const response = await apiClient.post<import('@/lib/types/creation').CreationGenerateResult>(
      `/agent-api/creation/prompts/${id}/generate`,
      { content, variables, assets },
    )
    return response.data
  },

  // ── Pipelines (background graph) ────────────────────────────────

  createPipeline: async (payload: CreatePipelinePayload) => {
    const response = await apiClient.post<PipelineCreateResult>(
      `${BASE}/pipelines`,
      payload,
    )
    return response.data
  },

  getPipeline: async (id: string) => {
    const response = await apiClient.get<PipelineDetail>(
      `${BASE}/pipelines/${id}`,
    )
    return response.data
  },

  listPipelines: async (params: {
    pipeline_type?: string
    status?: string
    limit?: number
    offset?: number
  } = {}) => {
    const response = await apiClient.get<{
      items: PipelineSummary[]
      total: number
      limit: number
      offset: number
    }>(`${BASE}/pipelines`, { params })
    return response.data
  },

  cancelPipeline: async (id: string) => {
    const response = await apiClient.post<PipelineDetail>(
      `${BASE}/pipelines/${id}/cancel`,
    )
    return response.data
  },
}
